export const MARKER_PIN_WIDTH_PX = 26;
export const MARKER_PIN_HEIGHT_PX = 36;
export const MARKER_PIN_PIXEL_RATIO = 2;

const SDF_RADIUS = 8;
const SDF_CUTOFF = 0.25;
const SDF_BUFFER = 8;

interface Vec {
  x: number;
  y: number;
}

export interface MarkerPinImage {
  width: number;
  height: number;
  data: Uint8Array;
}

/**
 * Distance signée (négative à l'intérieur) à une épingle : un disque prolongé par deux tangentes
 * jusqu'à la pointe, en bas au centre.
 */
export function markerPinDistance(point: Vec, width = MARKER_PIN_WIDTH_PX, height = MARKER_PIN_HEIGHT_PX): number {
  const radius = width / 2;
  const center = { x: width / 2, y: radius };
  const tip = { x: width / 2, y: height };
  const [left, right] = tangentPoints(center, radius, tip);

  return Math.min(Math.hypot(point.x - center.x, point.y - center.y) - radius, triangleDistance(point, [left, tip, right]));
}

/**
 * L'épingle en champ de distance (le format `sdf` de MapLibre) : `icon-color` la teinte, et
 * `icon-halo-*` la détoure, sans une image par couleur.
 */
export function createMarkerPinImage(pixelRatio = MARKER_PIN_PIXEL_RATIO): MarkerPinImage {
  const width = Math.ceil(MARKER_PIN_WIDTH_PX * pixelRatio) + 2 * SDF_BUFFER;
  const height = Math.ceil(MARKER_PIN_HEIGHT_PX * pixelRatio) + 2 * SDF_BUFFER;
  const data = new Uint8Array(width * height * 4);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const distance =
        markerPinDistance({ x: (x + 0.5 - SDF_BUFFER) / pixelRatio, y: (y + 0.5 - SDF_BUFFER) / pixelRatio }) * pixelRatio;
      const index = (y * width + x) * 4;
      data[index] = 255;
      data[index + 1] = 255;
      data[index + 2] = 255;
      data[index + 3] = Math.max(0, Math.min(255, Math.round(255 * (1 - SDF_CUTOFF - distance / SDF_RADIUS))));
    }
  }

  return { width, height, data };
}

/** Le contour de l'épingle en tracé SVG, pointe en bas au centre — même dessin que `markerPinDistance`. */
export function markerPinPath(width = MARKER_PIN_WIDTH_PX, height = MARKER_PIN_HEIGHT_PX): string {
  const radius = width / 2;
  const tip = { x: width / 2, y: height };
  const [left, right] = tangentPoints({ x: width / 2, y: radius }, radius, tip);
  const round = (value: number) => Math.round(value * 100) / 100;

  return `M${round(tip.x)} ${round(tip.y)} L${round(left.x)} ${round(left.y)} A${radius} ${radius} 0 1 1 ${round(right.x)} ${round(right.y)} Z`;
}

function tangentPoints(center: Vec, radius: number, from: Vec): [Vec, Vec] {
  const dx = center.x - from.x;
  const dy = center.y - from.y;
  const distance = Math.hypot(dx, dy);
  const toCenter = Math.atan2(dy, dx);
  const halfAngle = Math.asin(radius / distance);
  const length = Math.sqrt(distance * distance - radius * radius);

  return [-halfAngle, halfAngle].map((offset) => ({
    x: from.x + Math.cos(toCenter + offset) * length,
    y: from.y + Math.sin(toCenter + offset) * length,
  })) as [Vec, Vec];
}

function triangleDistance(point: Vec, triangle: [Vec, Vec, Vec]): number {
  let distance = Infinity;
  let inside = false;

  for (let i = 0, j = triangle.length - 1; i < triangle.length; j = i++) {
    const a = triangle[j]!;
    const b = triangle[i]!;
    distance = Math.min(distance, segmentDistance(point, a, b));
    if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }

  return inside ? -distance : distance;
}

function segmentDistance(point: Vec, a: Vec, b: Vec): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * abx + (point.y - a.y) * aby) / (abx * abx + aby * aby)));

  return Math.hypot(point.x - (a.x + t * abx), point.y - (a.y + t * aby));
}

const MARKER_MIN_COLOR_ALPHA = 0.5;

/**
 * Une épingle est pleine : la première couleur assez couvrante parmi `candidates`, rendue opaque.
 * Un fond de forme est souvent un voile (`#FFFFFF40`) qui la rendrait invisible.
 */
export function opaqueMarkerColor(candidates: string[]): string | null {
  for (const candidate of candidates) {
    const color = parseCssColor(candidate);
    if (color && color.alpha >= MARKER_MIN_COLOR_ALPHA) return color.opaque;
  }

  return null;
}

export function parseCssColor(value: string): { opaque: string; alpha: number } | null {
  const color = value.trim().toLowerCase();
  if (color === "transparent") return null;

  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(color)?.[1];
  if (hex) {
    const [red, green, blue, alpha = "ff"] = hex.length <= 4 ? [...hex].map((digit) => digit + digit) : hex.match(/../g)!;
    return { opaque: `#${red}${green}${blue}`, alpha: parseInt(alpha, 16) / 255 };
  }

  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+)(%)?)?\s*\)$/.exec(color);
  if (rgb) {
    const alpha = rgb[4] === undefined ? 1 : Number(rgb[4]) / (rgb[5] ? 100 : 1);
    return { opaque: `rgb(${rgb[1]}, ${rgb[2]}, ${rgb[3]})`, alpha };
  }

  return /^[a-z]+$/.test(color) ? { opaque: color, alpha: 1 } : null;
}
